import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbp, toDisplay, toGbp, curSymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { CalendarDays, Pencil } from "lucide-react";
import { CardHead, Field, FormDialog, niceDate, ordinal } from "@/components/prohub/shared";

function Stat({ label, value, tone = "text-white", testid }) {
  return (
    <div className="rounded-xl border border-white/8 bg-[#0D0D0D] p-3">
      <p className="text-[10px] text-slate-500">{label}</p>
      <p data-testid={testid} className={`font-num text-sm font-bold mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}

export default function PaydayCard({ data, onChanged }) {
  const { payday, cycle, summary } = data;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ take_home: "", payday_day: "" });
  const [saving, setSaving] = useState(false);

  const openEdit = () => { setForm({ take_home: payday ? String(toDisplay(payday.take_home)) : "", payday_day: payday ? String(payday.payday_day) : "25" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/pro-hub/payday", { take_home: toGbp(form.take_home), payday_day: parseInt(form.payday_day, 10) });
      toast.success("Payday saved");
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upcoming = data.bills.filter((b) => !b.paid).slice(0, 3);
  const used = summary ? Math.min(100, ((summary.spent_this_cycle + summary.bills_due_before_payday) / summary.take_home) * 100) : 0;
  const negative = summary && summary.left_to_spend < 0;

  return (
    <div className="mm-glow-card p-6 h-full" data-testid="pro-payday-card">
      <CardHead icon={CalendarDays} title="Payday planner" sub={payday ? `Paid on the ${ordinal(payday.payday_day)} · ${gbp(payday.take_home)} take-home` : "Plan your money between paydays"}
        action={payday && <button data-testid="pro-edit-payday" onClick={openEdit} className="text-slate-400 hover:text-white transition-colors"><Pencil className="h-4 w-4" /></button>} />

      {summary ? (
        <div className="mt-5">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs text-slate-500">Left to spend until payday</p>
              <p data-testid="pro-left-to-spend" className={`font-num text-4xl font-extrabold mt-1 ${negative ? "text-rose-400" : "text-white"}`}>{gbp(summary.left_to_spend)}</p>
            </div>
            <div className="sm:text-right">
              <p data-testid="pro-days-to-payday" className="font-num text-2xl font-bold text-[#00C896]">{cycle.days_to_payday}<span className="text-sm text-slate-400 font-sans font-normal"> {cycle.days_to_payday === 1 ? "day" : "days"}</span></p>
              <p className="text-[11px] text-slate-500">to payday · {niceDate(cycle.next_payday)}</p>
            </div>
          </div>
          <div className="h-2.5 rounded-full bg-white/5 overflow-hidden mt-4">
            <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${used}%`, background: negative ? "#F43F5E" : "#00C896" }} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Stat label="Spent this cycle" value={gbp(summary.spent_this_cycle)} testid="pro-spent-cycle" />
            <Stat label={`Bills before payday (${summary.bills_due_count})`} value={gbp(summary.bills_due_before_payday)} tone="text-amber-300" testid="pro-bills-before-payday" />
            <Stat label="Safe to spend / day" value={gbp(summary.safe_daily)} tone="text-[#4DE3BF]" testid="pro-safe-daily" />
          </div>
          <div className="mt-5" data-testid="pro-next-bills">
            <p className="text-xs text-slate-500 mb-2">Next bills to land</p>
            {upcoming.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-1.5 text-sm">
                <span className="flex items-center gap-2 min-w-0">
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${b.days_until_due <= 3 ? "bg-rose-400" : "bg-amber-300"}`} />
                  <span className="truncate">{b.name}</span>
                  <span className="text-[11px] text-slate-500 shrink-0">{niceDate(b.next_due)}</span>
                </span>
                <span className="font-num text-slate-300">{gbp(b.amount)}</span>
              </div>
            ))}
            {upcoming.length === 0 && <p className="text-sm text-slate-500">No unpaid bills coming up. Add or import bills below.</p>}
          </div>
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-dashed border-white/12 p-6 text-center">
          <p className="text-sm text-slate-400">Add your take-home pay and payday to see what's safe to spend each day.</p>
          <Button data-testid="pro-set-payday" onClick={openEdit} variant="outline" className="mt-3 rounded-full border-[#00C896]/40 bg-transparent text-[#00C896] hover:bg-[#00C896]/10">Set up payday</Button>
        </div>
      )}

      <FormDialog open={open} onOpenChange={setOpen} title="Your payday" testid="pro-payday-modal" onSave={save} saving={saving}
        disabled={!(parseFloat(form.take_home) > 0) || !(parseInt(form.payday_day, 10) >= 1 && parseInt(form.payday_day, 10) <= 31)} saveLabel="Save payday">
        <Field label={`Monthly take-home pay (${curSymbol()})`} testid="pro-take-home-input" type="number" min="1" value={form.take_home} onChange={(e) => setForm({ ...form, take_home: e.target.value })} placeholder="2800" />
        <Field label="Payday (day of month, 1–31)" testid="pro-payday-day-input" type="number" min="1" max="31" value={form.payday_day} onChange={(e) => setForm({ ...form, payday_day: e.target.value })} placeholder="25" />
        <p className="text-[11px] text-slate-500">If your payday falls after the end of a short month, we'll use the last day of that month.</p>
      </FormDialog>
    </div>
  );
}
